/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

/**
 * Version of the shadow document this build speaks.
 *
 * This is one half of the contract with the gateway; the other half is
 * internal/twin in ips-virtual-alpha-gateway. The two must be bumped together,
 * and a consumer seeing a version it does not know must refuse the document
 * rather than guess at the fields.
 */
export const SHADOW_SCHEMA_VERSION = 1;

/**
 * The Creative Look modes, as the short names used in the shadow. They are
 * derived from the device's own property catalog: "ST(Standard)" becomes "ST",
 * "Custom Look 1" becomes "CustomLook1".
 */
export const CreativeLookModeSchema = z.enum([
  'ST',
  'PT',
  'NT',
  'VV',
  'VV2',
  'FL',
  'IN',
  'SH',
  'BW',
  'SE',
  'FL2',
  'FL3',
  'CustomLook1',
  'CustomLook2',
  'CustomLook3',
  'CustomLook4',
  'CustomLook5',
  'CustomLook6',
]);
export type CreativeLookMode = z.infer<typeof CreativeLookModeSchema>;

/**
 * The only parameter group carried in the shadow.
 *
 * Ranges mirror the device's own property constraints, so the API rejects a
 * value the device would refuse instead of letting it round-trip to the device
 * and bounce back. Every field is optional: a desired document names a subset.
 */
export const CreativeLookSchema = z
  .object({
    mode: CreativeLookModeSchema.optional(),
    contrast: z.number().int().min(-9).max(9).optional(),
    highlights: z.number().int().min(-9).max(9).optional(),
    shadows: z.number().int().min(-9).max(9).optional(),
    fade: z.number().int().min(0).max(9).optional(),
    saturation: z.number().int().min(-9).max(9).optional(),
    sharpness: z.number().int().min(0).max(9).optional(),
    sharpnessRange: z.number().int().min(1).max(5).optional(),
    clarity: z.number().int().min(0).max(9).optional(),
  })
  .strict();
export type CreativeLook = z.infer<typeof CreativeLookSchema>;

export const VectorSchema = z
  .object({
    x: z.number(),
    y: z.number(),
    z: z.number(),
  })
  .strict();
export type Vector = z.infer<typeof VectorSchema>;

/**
 * Where the device sits in the world. Scene data owned by the cloud: the gateway
 * stores and echoes it but never maps it to a PTP property, because a real
 * device has no notion of its own position.
 */
export const TransformSchema = z
  .object({
    position: VectorSchema,
    rotation: VectorSchema,
  })
  .strict();
export type Transform = z.infer<typeof TransformSchema>;

export const IdentitySchema = z
  .object({
    model: z.string(),
    serial: z.string(),
    address: z.string(),
  })
  .strict();

export const SessionSchema = z
  .object({
    connected: z.boolean(),
    client: z.string().optional(),
    since: z.number().int().optional(),
  })
  .strict();

/**
 * The half of the shadow the cloud owns.
 *
 * Not strict: this describes a document being *read back*, and a field written
 * by a newer build must not cost the caller the fields it does understand.
 * Unknown input is rejected on the write path instead, by `WriteStateRequest`.
 */
/**
 * A shutter release on a virtual camera, waiting for someone showing the world to
 * render the picture. `at` is Unix milliseconds on the gateway's clock.
 */
export const CaptureRequestSchema = z.object({ id: z.string(), at: z.number() });
export type CaptureRequest = z.infer<typeof CaptureRequestSchema>;

/** The answer: a short-lived URL the gateway fetches the rendered still from. */
export const CaptureSchema = z.object({ id: z.string(), url: z.string() });

export const DesiredSchema = z.object({
  transform: TransformSchema.optional(),
  creativeLook: CreativeLookSchema.optional(),
  capture: CaptureSchema.optional(),
});
export type Desired = z.infer<typeof DesiredSchema>;

/** The half of the shadow the device owns. Not strict, for the same reason. */
export const ReportedSchema = z.object({
  schemaVersion: z.number().int(),
  identity: IdentitySchema,
  transform: TransformSchema.optional(),
  creativeLook: CreativeLookSchema.optional(),
  // Mirrors the device's grey-out state so the UI can disable a slider rather
  // than let the user set a value that will bounce.
  creativeLookEnabled: z.record(z.string(), z.boolean()).optional(),
  session: SessionSchema,
  captureRequest: CaptureRequestSchema.optional(),
});
export type Reported = z.infer<typeof ReportedSchema>;

/**
 * What `GetThingShadow` actually returns.
 *
 * AWS owns this envelope and puts more in it than we model: `metadata`,
 * `timestamp`, `clientToken`, and a `state.delta` whenever desired and reported
 * disagree — which is always, for a device with no gateway running. Nothing
 * here may be strict, or every real shadow fails to parse.
 *
 * Each half also absorbs its own failure, so a reported block this build cannot
 * read never hides a desired block it can.
 */
export const ShadowDocumentSchema = z.object({
  state: z.object({
    desired: DesiredSchema.optional().catch(undefined),
    reported: ReportedSchema.optional().catch(undefined),
  }),
  version: z.number().int().optional(),
  /** Mirrors `state`, with a `{ timestamp }` leaf per field. Shape is AWS's. */
  metadata: z.unknown().optional(),
});
export type ShadowDocument = z.infer<typeof ShadowDocumentSchema>;

/**
 * When the device last reported, from the metadata mirror of `reported`.
 *
 * Not the envelope's own `timestamp`: on a `GetThingShadow` response that is
 * when the *response* was generated, so it equals now on every read and would
 * make a twin nobody has touched in days look like it just spoke.
 */
export const newestTimestamp = (node: unknown): number | undefined => {
  if (typeof node !== 'object' || node === null) {
    return undefined;
  }

  let newest: number | undefined;
  for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
    const found = key === 'timestamp' && typeof value === 'number' ? value : newestTimestamp(value);
    if (found !== undefined && (newest === undefined || found > newest)) {
      newest = found;
    }
  }
  return newest;
};

/**
 * Prefix shared by every Thing in one world.
 *
 * A gateway policy grants a whole world by wildcarding this. The colon is what
 * makes that safe: neither a deployment name, a world id nor a device id may
 * contain one, so `{deployment}:{world_id}:` cannot run into another world's
 * names however those ids are generated.
 */
export const thingPrefix = (deployment: string, worldId: string): string => `${deployment}:${worldId}:`;

/**
 * Thing name of a device's cloud twin: `{deployment}:{world_id}:{device_id}`.
 *
 * The deployment leads so that several deployments can share one AWS account
 * without their twins colliding, matching how every other physical name in this
 * stack is composed.
 */
export const thingName = (deployment: string, worldId: string, deviceId: string): string =>
  `${thingPrefix(deployment, worldId)}${deviceId}`;

/**
 * The pseudo-device whose shadow tells a world's gateways which cameras to run.
 *
 * Not a UUID, so no device id can ever collide with it, and inside the world's
 * thing prefix, so the gateway policy that already covers the world's shadows
 * covers it too: gateways issued before the roster existed can read it.
 */
export const ROSTER_ID = 'roster';

export const rosterThingName = (deployment: string, worldId: string): string =>
  thingName(deployment, worldId, ROSTER_ID);

/**
 * The roster, as `desired` in the roster shadow. An array, so each write replaces
 * it whole: a removed device drops out without anyone having to null it.
 * `addr` is bare; the gateway adds the fixed PTP port.
 */
export const RosterSchema = z
  .object({
    cameras: z.array(z.object({ id: z.string(), model: z.string(), serial: z.string(), addr: z.string() }).strict()),
  })
  .strict();
export type Roster = z.infer<typeof RosterSchema>;

/**
 * Splits a Thing name back into the ids it was built from.
 *
 * The fan-out rule learns which device changed from the MQTT topic alone, so
 * this is the inverse of `thingName` and the one place that knowledge lives.
 * Returns undefined for a name from another deployment or another shape.
 */
export const parseThingName = (deployment: string, name: string): { worldId: string; deviceId: string } | undefined => {
  const parts = name.split(':');
  if (parts.length !== 3 || parts[0] !== deployment) {
    return undefined;
  }
  const [, worldId, deviceId] = parts;
  return worldId && deviceId ? { worldId, deviceId } : undefined;
};
